import { useEffect, useState } from 'react'

import { useArchiveFloor } from '../hooks/useArchiveFloor'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'
import { HiExclamation, HiOutlineArchive } from 'react-icons/hi'

import {
  Alert,
  AlertDialogBody,
  AlertDialogFooter,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  DarkProvider,
  Select as DarkSelect,
  Text as DarkText,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { BasicAlertDialog, Button, InputSelect } from '@/components/ui'
import {
  useFacilityFloorsDrawerQuery,
  usePreviewArchiveFloorQuery,
} from '@/graphql/generated/hooks'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import { GuardTourImpactAlert } from './GuardTourImpactAlert'

interface ArchiveFloorModalIProps {
  colorScheme?: ColorScheme
  isOpen: boolean
  floorId: string
  floorName: string
  facilityId: string
  onClose: () => void
  /** Called after a successful archive (e.g. to close the parent modal). */
  onArchived: () => void
}

/**
 * Confirmation dialog for archiving a floor. Archiving cascades to the
 * floor plan and devices on the backend. If the floor is the facility's
 * default floor, the backend requires a replacement — the dialog asks the
 * user to pick one and sends it as newDefaultFloorId in the same mutation.
 *
 * Archiving also reconciles guard tour plans (ENG-32005): plans spanning
 * other floors get a new version without this floor's checkpoints, plans
 * left empty are archived, and the backend rejects the archive while guard
 * tours using the floor's checkpoints are in progress. The dialog surfaces
 * that impact via the read-only previewArchiveFloor query and blocks the
 * confirm button while in-progress tours exist or until a preview has
 * loaded (a failed preview must not read as "no impact").
 */
export const ArchiveFloorModal = ({
  colorScheme,
  isOpen,
  floorId,
  floorName,
  facilityId,
  onClose,
  onArchived,
}: ArchiveFloorModalIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const me = useAppSelector(selectMe)
  const { shouldEnableUpdateFloor } = usePermissions()
  const { archiveFloor, isArchiveLoading } = useArchiveFloor()
  const [newDefaultFloorId, setNewDefaultFloorId] = useState('')

  // isDefaultFloor is unknown until this resolves; the archive button stays
  // disabled while loading so a default floor can't slip through without a
  // replacement (the backend would reject it).
  const { data, loading: isFloorsLoading } = useFacilityFloorsDrawerQuery({
    skip: !isOpen || !facilityId,
    variables: { facilityId },
  })

  // network-only: tours start and finish while the dialog is closed, and the
  // dialog stays mounted between opens — a cached preview could show a stale
  // "blocked" (or worse, a stale "clear") state on reopen.
  const {
    data: previewData,
    loading: isPreviewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = usePreviewArchiveFloorQuery({
    fetchPolicy: 'network-only',
    // Show loading progress on the Retry affordance below.
    notifyOnNetworkStatusChange: true,
    skip: !isOpen || !floorId,
    variables: { id: floorId },
  })

  useEffect(() => {
    if (!previewError) return
    Sentry.captureException(previewError, {
      tags: {
        action: 'preview_archive',
        feature: 'floor_crud',
        surface: 'archive_floor_modal',
      },
      extra: { floorId },
    })
  }, [previewError, floorId])

  const preview = previewData?.previewArchiveFloor
  const tourPlansToUpdateCount = preview?.tourPlansToUpdateCount ?? 0
  const tourPlansToArchiveCount = preview?.tourPlansToArchiveCount ?? 0
  const inProgressGuardTourCount = preview?.inProgressGuardTourCount ?? 0
  const isBlockedByGuardTours = inProgressGuardTourCount > 0
  // The preview is the only disclosure of the guard tour plan reconciliation
  // (the backend performs it silently on a successful archive), so a failed
  // preview must not read as "no impact" — confirmation stays disabled until
  // a preview has actually loaded, with a Retry affordance on error.
  const hasPreviewFailed = Boolean(previewError) && !isPreviewLoading

  const isDefaultFloor = data?.facility?.defaultFloorId === floorId
  const replacementFloorOptions = (data?.facility?.floors?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node) => Boolean(node) && node.id !== floorId)
    .map((node) => ({ label: node.name, value: node.id }))
  const selectedReplacementFloor =
    replacementFloorOptions.find(
      (option) => option.value === newDefaultFloorId
    ) ?? null

  // The dialog stays mounted between opens; clear the replacement selection
  // on every close so a reopen starts fresh.
  const handleClose = () => {
    setNewDefaultFloorId('')
    onClose()
  }

  const onSubmit = async () => {
    trackEvent('Button Press: Edit Floor - Archive Floor Save', {
      facility_id: facilityId,
      floor_id: floorId,
      role: me?.roleName,
    })
    const isArchived = await archiveFloor({
      floorId,
      newDefaultFloorId: isDefaultFloor ? newDefaultFloorId : undefined,
    })
    if (isArchived) {
      handleClose()
      onArchived()
    }
  }

  const handleCancel = () => {
    trackEvent('Button Press: Edit Floor - Archive Floor Cancel', {
      facility_id: facilityId,
      floor_id: floorId,
      role: me?.roleName,
    })
    handleClose()
  }

  return (
    <BasicAlertDialog
      isCentered
      isOpen={isOpen}
      onClose={handleClose}
      title={t('floors:archive_dialog_title')}
    >
      <AlertDialogBody>
        <Box
          color={`text.primary.${mode}`}
          data-testid='archiveFloorModal_message'
          fontSize='16px'
          fontWeight='500'
          letterSpacing='-0.53px'
        >
          {t('floors:archive_confirm')}
        </Box>
        <Box
          color={`text.primary.${mode}`}
          fontSize='16px'
          fontWeight='extrabold'
          letterSpacing='-0.4px'
          mt='2'
        >
          {floorName}
        </Box>
        <Flex
          alignItems='center'
          color={`text.alert-danger.${mode}`}
          flexDirection='row'
          pt='4'
        >
          <HiExclamation size='24px' />
          <Box fontSize='16px' fontWeight='medium' ml='1'>
            <Box as='span' fontWeight='bold'>
              {t('common:warning')}
            </Box>{' '}
            {t('floors:archive_cascade_warning')}
          </Box>
        </Flex>
        <GuardTourImpactAlert
          inProgressGuardTourCount={inProgressGuardTourCount}
          tourPlansToArchiveCount={tourPlansToArchiveCount}
          tourPlansToUpdateCount={tourPlansToUpdateCount}
        />
        {hasPreviewFailed && (
          <Alert
            alignItems='center'
            borderRadius='10px'
            color={`text.alert-danger.${mode}`}
            data-testid='archiveFloorModal_previewError'
            mt='4'
            status='error'
          >
            <Icon as={HiExclamation} boxSize='20px' flexShrink={0} mr='10px' />
            <Box fontSize='sm' fontWeight='medium'>
              {t('floors:archive_preview_error')}
            </Box>
            <Button
              data-testid='archiveFloorModal_previewRetryButton'
              ml='3'
              onClick={() => refetchPreview()}
              size='sm'
              variant='ghost'
            >
              {t('common:retry')}
            </Button>
          </Alert>
        )}
        {isDefaultFloor &&
          (colorScheme ? (
            <DarkProvider colorScheme={colorScheme}>
              <DarkBox mt='16px'>
                <DarkText
                  color={
                    colorScheme === 'light'
                      ? 'light.text.primary'
                      : 'text.primary'
                  }
                  mb='8px'
                  textStyle='body'
                >
                  {t('floors:archive_default_floor_body')}
                </DarkText>
                <DarkSelect
                  colorScheme={colorScheme}
                  data-testid='archiveFloorModal_newDefaultFloorSelect'
                  inputId='archiveFloor_newDefaultFloor'
                  label={t('floors:archive_new_default_label')}
                  onChange={(option) =>
                    setNewDefaultFloorId(option?.value ?? '')
                  }
                  options={replacementFloorOptions}
                  placeholder={t('floors:archive_new_default_placeholder')}
                  value={selectedReplacementFloor}
                />
              </DarkBox>
            </DarkProvider>
          ) : (
            <FormControl mt='4'>
              <Box fontSize='16px' fontWeight='500' mb='2'>
                {t('floors:archive_default_floor_body')}
              </Box>
              <FormLabel fontSize='14px' htmlFor='archiveFloor_newDefaultFloor'>
                {t('floors:archive_new_default_label')}
              </FormLabel>
              <Box data-testid='archiveFloorModal_newDefaultFloorSelect'>
                <InputSelect
                  canCreateOption={false}
                  inputId='archiveFloor_newDefaultFloor'
                  onChange={(option: { value: string } | null) =>
                    setNewDefaultFloorId(option?.value ?? '')
                  }
                  options={replacementFloorOptions}
                  placeholder={t('floors:archive_new_default_placeholder')}
                  value={selectedReplacementFloor}
                />
              </Box>
            </FormControl>
          ))}
      </AlertDialogBody>
      <AlertDialogFooter>
        <Button
          data-testid='archiveFloorModal_cancelButton'
          mr='3'
          onClick={handleCancel}
          variant='ghost'
        >
          {t('common:cancel')}
        </Button>
        <Button
          data-testid='archiveFloorModal_archiveButton'
          isDisabled={
            isArchiveLoading ||
            isFloorsLoading ||
            isPreviewLoading ||
            !preview ||
            isBlockedByGuardTours ||
            !shouldEnableUpdateFloor ||
            (isDefaultFloor && !newDefaultFloorId)
          }
          isLoading={isArchiveLoading}
          leftIcon={<HiOutlineArchive size='24px' />}
          onClick={onSubmit}
          variant='danger'
        >
          {t('floors:archive_button')}
        </Button>
      </AlertDialogFooter>
    </BasicAlertDialog>
  )
}
